import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Heartbeat, Plus, Trash, ChartBar, Prohibit, WarningCircle, CheckCircle, XCircle, Signature, FilePdf } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SignaturePad from "@/components/SignaturePad";
import SectionHeader from "@/components/SectionHeader";

const REASONS = [
  { v: "entrada_turno", l: "Entrada al turno" },
  { v: "random", l: "Aleatorio en el turno" },
  { v: "post_incidente", l: "Post incidente" },
];

const STATUS_STYLE = {
  apto: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle, l: "Apto" },
  restringido: { color: "bg-amber-100 text-amber-800 border-amber-300", icon: WarningCircle, l: "Restringido" },
  no_apto: { color: "bg-red-100 text-red-800 border-red-400", icon: Prohibit, l: "No Apto · Bloqueado" },
};

function MeasureRow({ spec, testKey, value, onChange, onRemove }) {
  const [restrText, setRestrText] = useState("");
  const failed = value.passed === false;

  const addRestr = () => {
    const t = restrText.trim();
    if (!t) return;
    onChange({ ...value, restricted_activities: [...(value.restricted_activities || []), t] });
    setRestrText("");
  };
  const rmRestr = (i) => onChange({ ...value, restricted_activities: (value.restricted_activities || []).filter((_, j) => j !== i) });

  return (
    <div className={`border rounded p-3 space-y-2 ${failed ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-white"}`} data-testid={`med-measure-${testKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-bold text-sm text-slate-900">{spec.label}</p>
          <p className="text-[10px] text-slate-500 italic">{spec.hint}</p>
        </div>
        <button onClick={onRemove} title="Quitar prueba" className="text-red-500 hover:text-red-700" data-testid={`med-remove-${testKey}`}>
          <Trash size={14} />
        </button>
      </div>
      {testKey === "presion_arterial" ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="overline-label text-[9px]">Sistólica</Label>
            <Input type="number" step="1" min="0" value={value.systolic ?? ""} onChange={(e) => onChange({ ...value, systolic: e.target.value === "" ? null : Number(e.target.value) })}
                   className="h-9" data-testid={`med-${testKey}-sys`} />
          </div>
          <div>
            <Label className="overline-label text-[9px]">Diastólica</Label>
            <Input type="number" step="1" min="0" value={value.diastolic ?? ""} onChange={(e) => onChange({ ...value, diastolic: e.target.value === "" ? null : Number(e.target.value) })}
                   className="h-9" data-testid={`med-${testKey}-dia`} />
          </div>
        </div>
      ) : testKey === "peso_imc" ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="overline-label text-[9px]">Peso (kg)</Label>
            <Input type="number" step="0.1" min="0" value={value.weight_kg ?? ""} onChange={(e) => onChange({ ...value, weight_kg: e.target.value === "" ? null : Number(e.target.value) })}
                   className="h-9" data-testid={`med-${testKey}-w`} />
          </div>
          <div>
            <Label className="overline-label text-[9px]">IMC</Label>
            <Input type="number" step="0.1" min="0" value={value.imc ?? ""} onChange={(e) => onChange({ ...value, imc: e.target.value === "" ? null : Number(e.target.value) })}
                   className="h-9" data-testid={`med-${testKey}-imc`} />
          </div>
        </div>
      ) : (
        <div>
          <Label className="overline-label text-[9px]">Resultado ({spec.unit})</Label>
          <Input type="number" step="0.01" min="0" value={value.value ?? ""} onChange={(e) => onChange({ ...value, value: e.target.value === "" ? null : Number(e.target.value) })}
                 className="h-9" data-testid={`med-${testKey}-value`} placeholder={spec.unit} />
        </div>
      )}
      <div className="flex items-center gap-2">
        <button onClick={() => onChange({ ...value, passed: true })}
                data-testid={`med-${testKey}-pass`}
                className={`px-3 py-1 rounded text-xs font-bold ${value.passed !== false ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          Aprobado
        </button>
        <button onClick={() => onChange({ ...value, passed: false })}
                data-testid={`med-${testKey}-fail`}
                className={`px-3 py-1 rounded text-xs font-bold ${failed ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          Falla
        </button>
        {spec.blocks_shift_on_fail && failed && (
          <span className="text-[10px] font-bold uppercase text-red-700 bg-red-100 px-2 py-1 rounded border border-red-300 inline-flex items-center gap-1">
            <Prohibit size={12} weight="bold" /> Bloquea turno
          </span>
        )}
      </div>
      {failed && (
        <div className="pt-2 border-t border-red-200 space-y-2">
          <div>
            <Label className="overline-label text-[9px]">Motivo / Observación</Label>
            <Input value={value.reason || ""} onChange={(e) => onChange({ ...value, reason: e.target.value })}
                   placeholder="Descripción clínica" className="h-9 text-xs" data-testid={`med-${testKey}-reason`} />
          </div>
          {!spec.blocks_shift_on_fail && (
            <div>
              <Label className="overline-label text-[9px]">Actividades restringidas (definidas por el paramédico)</Label>
              <div className="flex gap-1 mt-1">
                <Input value={restrText} onChange={(e) => setRestrText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRestr()}
                       placeholder="Ej: operar equipo pesado, trabajos en altura"
                       className="h-8 text-xs flex-1" data-testid={`med-${testKey}-restr-input`} />
                <Button size="sm" variant="outline" onClick={addRestr} className="h-8">+ Añadir</Button>
              </div>
              <ul className="mt-2 space-y-1">
                {(value.restricted_activities || []).map((a, i) => (
                  <li key={i} className="text-[11px] flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    <span>{a}</span>
                    <button onClick={() => rmRestr(i)} className="text-red-500 hover:text-red-700 font-bold">×</button>
                  </li>
                ))}
                {(value.restricted_activities || []).length === 0 && (
                  <li className="text-[10px] text-slate-400 italic">Sin restricciones definidas aún</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateTestDialog({ open, onOpenChange, catalog, employees, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    employee_name: "", employee_dni: "", employee_id: "", area: "",
    shift: "dia", date: today, time: new Date().toTimeString().slice(0, 5),
    reason: "entrada_turno", technician_name: "", technician_signature: "",
    notes: "",
  });
  const [measures, setMeasures] = useState([]);
  const [addKey, setAddKey] = useState("alcoholemia");
  const [busy, setBusy] = useState(false);

  const availableKeys = useMemo(
    () => Object.keys(catalog).filter((k) => !measures.some((m) => m.test_type === k)),
    [catalog, measures],
  );

  useEffect(() => { if (availableKeys.length && !availableKeys.includes(addKey)) setAddKey(availableKeys[0]); }, [availableKeys, addKey]);

  const addMeasure = () => {
    if (!addKey) return;
    setMeasures((p) => [...p, { test_type: addKey, passed: true, restricted_activities: [] }]);
  };

  const updateMeasure = (i, v) => setMeasures((p) => p.map((m, j) => j === i ? v : m));
  const removeMeasure = (i) => setMeasures((p) => p.filter((_, j) => j !== i));

  const submit = async () => {
    if (!form.employee_name.trim()) { toast.warning("Nombre del colaborador requerido"); return; }
    if (!form.technician_name.trim()) { toast.warning("Nombre del paramédico requerido"); return; }
    if (!form.technician_signature) { toast.warning("Firma del paramédico requerida"); return; }
    if (measures.length === 0) { toast.warning("Añade al menos una prueba"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/hse/medical/tests", { ...form, measures });
      if (data.blocks_shift) {
        toast.error(`🚫 BLOQUEO DE TURNO · ${data.employee_name} — se creó acción HSE crítica`);
      } else if (data.overall_status === "restringido") {
        toast.warning(`⚠ Restringido — ${(data.restricted_activities || []).length} actividad(es) limitada(s)`);
      } else {
        toast.success(`✓ Prueba médica registrada · ${data.employee_name} · Apto`);
      }
      onCreated?.(data);
      onOpenChange(false);
      setMeasures([]);
      setForm({ ...form, employee_name: "", employee_dni: "", employee_id: "", area: "", notes: "", technician_signature: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="med-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Heartbeat size={20} weight="duotone" className="text-red-600" /> Registrar prueba médica
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="col-span-2 md:col-span-1">
              <Label className="overline-label text-[9px]">Colaborador (desde Nómina) <span className="text-red-600">*</span></Label>
              <Select value={form.employee_id || "__none__"} onValueChange={(v) => {
                if (v === "__none__") { setForm({ ...form, employee_id: "", employee_name: "", employee_dni: "", area: form.area }); return; }
                const emp = (employees || []).find((e) => e.id === v);
                if (emp) setForm({
                  ...form, employee_id: emp.id,
                  employee_name: emp.name || "",
                  employee_dni: emp.dni || "",
                  area: form.area || emp.area || "",
                });
              }}>
                <SelectTrigger data-testid="med-emp-select" className="h-9"><SelectValue placeholder="Selecciona colaborador…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="__none__">— Sin vincular (ad-hoc) —</SelectItem>
                  {(employees || []).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}{e.dni ? ` · ${e.dni}` : ""}{e.area ? ` · ${e.area}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!form.employee_id && (
                <Input value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                       placeholder="…o escribe el nombre (ad-hoc)"
                       className="h-8 text-xs mt-1" data-testid="med-emp-name" />
              )}
            </div>
            <div>
              <Label className="overline-label text-[9px]">DNI</Label>
              <Input value={form.employee_dni} onChange={(e) => setForm({ ...form, employee_dni: e.target.value })}
                     data-testid="med-emp-dni" disabled={!!form.employee_id} />
            </div>
            <div>
              <Label className="overline-label text-[9px]">Área / labor</Label>
              <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
                     data-testid="med-area" />
            </div>
            <div>
              <Label className="overline-label text-[9px]">Fecha</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="med-date" />
            </div>
            <div>
              <Label className="overline-label text-[9px]">Hora</Label>
              <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                     data-testid="med-time" />
            </div>
            <div>
              <Label className="overline-label text-[9px]">Turno</Label>
              <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                <SelectTrigger data-testid="med-shift"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="dia">Día</SelectItem>
                  <SelectItem value="noche">Noche</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label text-[9px]">Motivo</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger data-testid="med-reason"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline-label text-[9px]">Paramédico / técnico</Label>
              <Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })}
                     data-testid="med-tech-name" placeholder="Nombre completo" />
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Pruebas aplicadas</p>
              <div className="flex items-center gap-1">
                <Select value={addKey} onValueChange={setAddKey}>
                  <SelectTrigger className="h-8 w-56 text-xs" data-testid="med-add-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {availableKeys.map((k) => (
                      <SelectItem key={k} value={k}>{catalog[k]?.label || k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={addMeasure} disabled={!availableKeys.length}
                        data-testid="med-add-btn" className="h-8 bg-blue-900 hover:bg-blue-800 gap-1">
                  <Plus size={12} weight="bold" /> Añadir
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {measures.map((m, i) => (
                <MeasureRow key={i} testKey={m.test_type} spec={catalog[m.test_type] || {}}
                            value={m} onChange={(v) => updateMeasure(i, v)}
                            onRemove={() => removeMeasure(i)} />
              ))}
              {measures.length === 0 && (
                <p className="text-xs text-slate-400 italic col-span-2 py-4 text-center">
                  Añade pruebas del catálogo. Al menos una es requerida.
                </p>
              )}
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Firma del paramédico <span className="text-red-600">*</span></p>
            <p className="text-[10px] text-slate-500 italic mb-2">La firma es requerida como respaldo legal del examen.</p>
            <div className="border border-slate-300 rounded bg-white p-2 max-w-md">
              <SignaturePad value={form.technician_signature} onChange={(v) => setForm({ ...form, technician_signature: v })}
                            width={340} height={120} testid="med-tech-sign" />
            </div>
          </div>

          <div>
            <Label className="overline-label text-[9px]">Notas adicionales</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   placeholder="Observaciones generales" data-testid="med-notes" />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.technician_signature || !form.employee_name || !form.technician_name || measures.length === 0}
                    data-testid="med-submit" className="bg-red-700 hover:bg-red-800 gap-2">
              <Signature size={14} weight="bold" /> {busy ? "Guardando…" : "Registrar prueba"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicalTestsModule() {
  const today = new Date().toISOString().slice(0, 10);
  const [catalog, setCatalog] = useState({});
  const [employees, setEmployees] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(today);
  const [tests, setTests] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, t, d, emps] = await Promise.all([
        api.get("/hse/medical/catalog").then((r) => r.data.tests || {}),
        api.get(`/hse/medical/tests?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => r.data || []),
        api.get(`/hse/medical/dashboard?date_from=${dateFrom}&date_to=${dateTo}`).then((r) => r.data),
        // Endpoint dedicado bajo `hse.view` — evita el 403 que da `/hr/employees`
        // a los usuarios de SGSSO que no tienen permiso `hr.view`.
        api.get("/hse/medical/employees").then((r) => r.data || []).catch((err) => {
          console.warn("[Medical] Fallo cargando directorio nómina:", err?.response?.status);
          return [];
        }),
      ]);
      setCatalog(c); setTests(t); setDashboard(d); setEmployees(emps);
    } catch (e) { toast.error(apiError(e)); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar registro de prueba médica?")) return;
    try {
      await api.delete(`/hse/medical/tests/${id}`);
      toast.success("Prueba eliminada"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="medical-tests-module">
      <SectionHeader icon={Heartbeat} title="Pruebas Médicas del Turno" eyebrow="Alcoholemia · Presión · SpO₂ · Glucosa · Fatiga · IMC" testid="med-header"
        right={
          <>
            <div>
              <Label className="overline-label text-[9px]">Desde</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="med-range-from" />
            </div>
            <div>
              <Label className="overline-label text-[9px]">Hasta</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="med-range-to" />
            </div>
            <Button onClick={() => setOpen(true)} data-testid="med-new-btn"
                    className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
              <Plus size={14} weight="bold" /> Nueva prueba
            </Button>
          </>
        } />

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="med-dashboard">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <p className="text-[10px] uppercase text-slate-500 font-bold">Total pruebas</p>
            <p className="text-2xl font-black text-slate-900">{dashboard.total}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] uppercase text-emerald-700 font-bold flex items-center gap-1"><CheckCircle size={11} weight="bold" /> Aptos</p>
            <p className="text-2xl font-black text-emerald-800">{dashboard.apto}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-[10px] uppercase text-amber-700 font-bold flex items-center gap-1"><WarningCircle size={11} weight="bold" /> Restringidos</p>
            <p className="text-2xl font-black text-amber-800">{dashboard.restringido}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-[10px] uppercase text-red-700 font-bold flex items-center gap-1"><XCircle size={11} weight="bold" /> No aptos</p>
            <p className="text-2xl font-black text-red-800">{dashboard.no_apto}</p>
          </div>
          <div className="bg-red-100 border border-red-400 rounded-lg p-3">
            <p className="text-[10px] uppercase text-red-800 font-bold flex items-center gap-1"><Prohibit size={11} weight="bold" /> Bloqueos</p>
            <p className="text-2xl font-black text-red-900">{dashboard.blocked}</p>
          </div>
        </div>
      )}

      {dashboard && Object.keys(dashboard.by_type || {}).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="med-by-type">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
            <ChartBar size={12} weight="bold" /> Fallas por tipo de prueba
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(dashboard.by_type).map(([k, v]) => {
              const failRate = v.count > 0 ? Math.round(v.failed / v.count * 100) : 0;
              return (
                <div key={k} className="border border-slate-200 rounded p-2">
                  <p className="text-[11px] font-bold text-slate-800">{catalog[k]?.label || k}</p>
                  <p className="text-[10px] text-slate-500">{v.failed}/{v.count} fallas · {failRate}%</p>
                  <div className="h-1.5 bg-slate-200 rounded mt-1 overflow-hidden">
                    <div className={`h-full ${failRate > 20 ? "bg-red-500" : failRate > 5 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${failRate}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="med-table">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="text-left px-3 py-2">Fecha · Hora</th>
              <th className="text-left px-3 py-2">Colaborador</th>
              <th className="text-left px-3 py-2">Área · Turno</th>
              <th className="text-left px-3 py-2">Pruebas</th>
              <th className="text-center px-3 py-2">Estatus</th>
              <th className="text-left px-3 py-2">Paramédico</th>
              <th className="text-center px-3 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {tests.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400 italic">Sin pruebas en el rango</td></tr>
            )}
            {tests.map((t) => {
              const st = STATUS_STYLE[t.overall_status] || STATUS_STYLE.apto;
              const Icon = st.icon;
              return (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`med-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{t.date}</p>
                    <p className="text-[10px] text-slate-400">{t.time || "—"}</p>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{t.employee_name}</p>
                    {t.employee_dni && <p className="text-[10px] text-slate-400">DNI {t.employee_dni}</p>}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {t.area || "—"}
                    <p className="text-[10px] text-slate-400 uppercase">{t.shift}</p>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(t.measures || []).map((m, i) => (
                        <span key={i}
                              title={`${catalog[m.test_type]?.label || m.test_type} · ${m.passed ? "OK" : "Falla"}`}
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${m.passed ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-300"}`}>
                          {catalog[m.test_type]?.label || m.test_type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded border ${st.color}`}>
                      <Icon size={11} weight="bold" /> {st.l}
                    </span>
                    {t.blocks_shift && (
                      <p className="text-[9px] text-red-700 font-bold mt-1">Acción HSE creada</p>
                    )}
                    {(t.restricted_activities || []).length > 0 && !t.blocks_shift && (
                      <button onClick={() => setSelected(t)} className="text-[10px] text-amber-800 underline mt-1" data-testid={`med-view-restr-${t.id}`}>
                        Ver {t.restricted_activities.length} restricción(es)
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{t.technician_name}</td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button onClick={async () => {
                      try {
                        const r = await api.get(`/hse/medical/tests/${t.id}/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url; a.download = `prueba-medica-${t.employee_name.replace(/ /g, "_")}-${t.date}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                        toast.success("PDF descargado");
                      } catch (e) { toast.error(apiError(e)); }
                    }} className="text-blue-600 hover:text-blue-800 mr-2" title="Descargar PDF (folder personal)" data-testid={`med-pdf-${t.id}`}>
                      <FilePdf size={14} weight="bold" />
                    </button>
                    <button onClick={() => remove(t.id)} className="text-red-500 hover:text-red-700" data-testid={`med-del-${t.id}`}>
                      <Trash size={13} weight="bold" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateTestDialog open={open} onOpenChange={setOpen} catalog={catalog} employees={employees} onCreated={load} />

      {selected && (
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Restricciones · {selected.employee_name}</DialogTitle></DialogHeader>
            <ul className="space-y-1">
              {(selected.restricted_activities || []).map((r, i) => (
                <li key={i} className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2">{r}</li>
              ))}
            </ul>
            {selected.notes && <p className="text-xs text-slate-600 italic mt-3">{selected.notes}</p>}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
